import * as Constants from './Constants';
import type { PerformanceDatapoint } from 'owa-analytics';
import endImageLoadPerfDataPoint from './endImageLoadPerfDataPoint';
import type { ResponseData } from './getUrlResponseData';
import loadFromUrl from './loadFromUrl';
import logDiagnosticData from './logDiagnosticData';
import logImageLoadError from './logImageLoadError';
import isElementAttached from './isElementAttached';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * The Retry context
 */
export interface RetryContext {
    imageType: string;
    howLoad: string;
    context: string;
    loaderCode: string;
    originalSrc: string;
    owaDomain: string;
    downloadDomain: string;
    responseData: ResponseData;
    perfDataPoint: PerformanceDatapoint;
}

/**
 * The retry status
 */
enum RetryStatus {
    NotStarted,
    Pending,
    Completed,
}

/**
 * The re-try item
 */
interface RetryItem {
    image: HTMLImageElement;
    url: string;
    retryContext: RetryContext;
    currentRetry: number;
    status: RetryStatus;
    whenQueued: number;
    onSuccessCallback?: (imageElement: HTMLImageElement) => void;
    onErrorCallback?: (imageElement: HTMLImageElement) => void;
}

// Max number of download that can happen at one time -- to avoid exhaust concurrent connection count
const MAX_CONCURRENT_DOWNLOAD = 4;

// Max re-try count
const MAX_RETRY_COUNT = 3;

// The re-try interval - 1s
const RETRY_INTERVAL = 1000;

// The re-try timeout - 20s
const RETRY_TIMEOUT = 20000;

// The 1px data uri to use during retry
const DATAURI_PLACEHOLDER =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAIBTAA7';

// Error retry timeout
const ErrorRetryTimedout = 'RetryTimedOut';

let retryQueue: RetryItem[] = [];
let jobid = null;

/**
 * Re-try load an inline image
 */
export default function retryLoadImage(
    image: HTMLImageElement,
    url: string,
    retryContext: RetryContext,
    onSuccessCallback?: (imageElement: HTMLImageElement) => void,
    onErrorCallback?: (imageElement: HTMLImageElement) => void
) {
    const retryItem = {
        image: image,
        url: url,
        retryContext: retryContext,
        currentRetry: 0,
        status: RetryStatus.NotStarted,
        whenQueued: Date.now(),
        onSuccessCallback,
        onErrorCallback,
    };

    cleanItemForRetry(retryItem);
    retryQueue.push(retryItem);
    scheduleRetryLoadImages();
}

/**
 * This reset image src to a start url, i.e. a 1px data uri
 * loadFromUrl has done removal of onload/onerror callback so we don't need to do it here again
 */
function cleanItemForRetry(retryItem: RetryItem) {
    retryItem.status = RetryStatus.NotStarted;
    retryItem.image.src = DATAURI_PLACEHOLDER;
}

/**
 * Complete re-try an item
 */
function completeRetryItem(retryItem: RetryItem, isSuccess: boolean, errorOverride: string = '') {
    // If already completed, return right away
    if (retryItem.status == RetryStatus.Completed) {
        return;
    }

    const { image, onSuccessCallback, onErrorCallback, currentRetry } = retryItem;
    if (isSuccess) {
        if (onSuccessCallback) {
            onSuccessCallback(image);
        }
    } else {
        if (onErrorCallback) {
            onErrorCallback(image);
        }
    }

    const {
        imageType,
        perfDataPoint,
        howLoad,
        context,
        loaderCode,
        originalSrc,
        owaDomain,
        downloadDomain,
        responseData,
    } = retryItem.retryContext;
    const {
        responseCode,
        status,
        contentType,
        detectedFormat,
        first10BytesBase64,
        mimeMatched,
    } = responseData;
    const error = isSuccess ? '' : errorOverride || status || responseCode.toString();
    if (perfDataPoint) {
        endImageLoadPerfDataPoint(
            perfDataPoint,
            imageType,
            howLoad,
            error,
            context,
            loaderCode,
            currentRetry.toString()
        );
    }

    if (!isSuccess) {
        logImageLoadError(
            imageType /* type */,
            howLoad /* howLoad */,
            error /* error */,
            responseCode.toString() /* statusCode*/,
            context /* context */,
            loaderCode /* loaderCode*/,
            originalSrc /* originalSrc*/,
            contentType /* contentType */,
            owaDomain,
            downloadDomain,
            detectedFormat,
            first10BytesBase64,
            mimeMatched.toString(),
            currentRetry.toString()
        );

        // Bug 27136: Enable error reporting in SDF when there is error for loading inline image (for errors like AttNotFound, NotImage, 404 etc.)
        // This change is temporary. We would like to get some real repro for the error to troubleshoot the issue
        if (isFeatureEnabled('mc-imageDiagnostics') && responseCode == 200) {
            logDiagnosticData(image, error, responseData);
        }
    }

    retryItem.status = RetryStatus.Completed;
}

/**
 * Schedule load next batch of images
 * This should be called when:
 * - a new item gets queued
 * - completing load of an image, both error and success
 */
function scheduleRetryLoadImages() {
    // Only schedule the job when it is not scheduled yet
    if (!jobid) {
        jobid = setTimeout(() => pumpRetry(), RETRY_INTERVAL);
    }
}

/**
 * Pump for re-try
 */
function pumpRetry() {
    if (retryQueue.length > 0) {
        // Get the current to evaluate against each item
        // We time out retry if it is not completed certain amount
        // of time after it is first queued
        const pumpTime = Date.now();
        const updatedQueue: RetryItem[] = [];
        const candidateQueue: RetryItem[] = [];
        const pendingQueue: RetryItem[] = [];

        retryQueue.forEach((item: RetryItem) => {
            if (item.status != RetryStatus.Completed) {
                if (pumpTime - item.whenQueued > RETRY_TIMEOUT) {
                    // Timeout the retry
                    completeRetryItem(item, false /* false */, ErrorRetryTimedout);
                } else if (item.status == RetryStatus.Pending) {
                    // This is items that are being re-tried
                    pendingQueue.push(item);
                    updatedQueue.push(item);
                } else if (item.status == RetryStatus.NotStarted) {
                    // Candidates for re-try
                    candidateQueue.push(item);
                    updatedQueue.push(item);
                }
            }
        });

        const newPickCount = MAX_CONCURRENT_DOWNLOAD - pendingQueue.length;
        if (newPickCount > 0 && candidateQueue.length > 0) {
            retryLoadImages(candidateQueue.slice(0, Math.min(newPickCount, candidateQueue.length)));
        }

        // updatedQueue is new queue
        retryQueue = updatedQueue;
    }

    // Schedule for next run as long as the retry queue is not drained
    if (retryQueue.length > 0) {
        jobid = setTimeout(() => pumpRetry(), RETRY_INTERVAL);
    } else {
        // Reset the job
        jobid = null;
    }
}

/**
 * Load next set of images for re-try
 */
function retryLoadImages(retryItems: RetryItem[]) {
    if (retryItems && retryItems.length > 0) {
        retryItems.forEach(item => {
            item.currentRetry++;
            item.status = RetryStatus.Pending;
            // Check if element is still attached
            if (isElementAttached(item.image)) {
                loadFromUrl(
                    item.image,
                    item.url,
                    (imageElement: HTMLImageElement) => {
                        // onSuccess
                        completeRetryItem(item, true /* isSuccess */);
                    },
                    (imageElement: HTMLImageElement) => {
                        if (item.currentRetry >= MAX_RETRY_COUNT) {
                            // Done with re-try, log error
                            completeRetryItem(item, false /* isSuccess */);
                        } else {
                            // Can still retry, reset its status for re-try to happen
                            cleanItemForRetry(item);
                        }
                    }
                );
            } else {
                // Log as detached. The error code will be something like DOMDetached_Rx so that
                // we could distinguish it from a normal DOMDetached
                completeRetryItem(
                    item,
                    false /* isSuccess */,
                    `${Constants.ERROR_DOM_DETACHED}_R${item.currentRetry}`
                );
            }
        });
    }
}
